import { t } from "i18next";

import {
  MIN_DATE_VALUE,
  RideStartEndOppositeKey,
  SINGLE_KEY,
} from "../../constants";
import { type IRideDto, type IRideMetadata } from "../../dto";
import { DirectionEnum, type TimeZoneEnum } from "../../enums";
import { type TRideDtoStartEndKey } from "../../models";
import {
  addMinutes,
  dateTimeWithoutSeconds,
  isValidDateTime,
  timeDiffMinutesSigned,
} from "../date.helpers";
import { Log } from "../log.helpers";
import {
  isUpdatedStationTimeInsideShiftLimits,
  updateMetadataStationsTime,
} from "./rides.recount.helpers";

interface IRideStartEndChangeHandlerArguments {
  rideDto?: Partial<IRideDto>;
  updatedRideDateTime: TDateISO;
  rideStartEndKey: TRideDtoStartEndKey;
  timeFormat: string;
  timezone: TimeZoneEnum;
  allowShiftTimeOverrideDuringManualAction?: boolean;
}

export interface IRideStartEndChangeHandlerValidResult {
  newOppositeStartEndRideValue: TDateISO;
  metadataUpdated: IRideMetadata;
}

export const isRideStartEndChangeHandlerValidResult = (
  value: NonNullable<unknown>,
): boolean => {
  return "newOppositeStartEndRideValue" in value && "metadataUpdated" in value;
};

export type TRideStartEndChangeHandlerResult =
  null | "revert" | IRideStartEndChangeHandlerValidResult;

export const rideStartEndChangeHandler = ({
  rideDto,
  updatedRideDateTime,
  rideStartEndKey,
  timeFormat,
  timezone,
  allowShiftTimeOverrideDuringManualAction = false,
}: IRideStartEndChangeHandlerArguments): TRideStartEndChangeHandlerResult => {
  if (!rideDto?.[rideStartEndKey]) {
    Log.warnAndToast(
      t("errors.invalidEntityDTOSupplied", { entity: t(`ride.${SINGLE_KEY}`) }),
    );
    return null;
  }

  const stations = rideDto.metadata?.stationsInfo;

  if (!stations?.length) {
    Log.debug("Ride without stations, no need to update stations time"); // TODO?
    return null;
  }

  if (!updatedRideDateTime) {
    Log.warnAndToast(t("errors.invalidTimeFormat"));
    return null;
  }

  const isValid = isValidDateTime(updatedRideDateTime);

  if (!isValid) {
    Log.warnAndToast(t("errors.invalidTimeFormat"));

    return "revert";
  }

  const timeOffsetMinutes = timeDiffMinutesSigned(
    dateTimeWithoutSeconds(rideDto[rideStartEndKey]),
    dateTimeWithoutSeconds(updatedRideDateTime),
  );

  if (!timeOffsetMinutes) {
    Log.debug("No time change defined", timeOffsetMinutes);
    return null;
  }

  const isTimeChangeCheckedByShiftTime = isUpdatedStationTimeInsideShiftLimits({
    rideDto,
    timeOffsetMinutes,
    timeFormat,
    timezone,
    allowShiftTimeOverrideDuringManualAction,
  });

  if (!isTimeChangeCheckedByShiftTime) {
    Log.debug(
      "Time edit changes REJECTED by shift time limits, minutes of shift:",
      timeOffsetMinutes,
    );
    return null;
  }

  Log.debug(
    "Time edit changes ACCEPTED by shift time limits, minutes of shift:",
    timeOffsetMinutes,
  );

  const oppositeRideStartEndKey = RideStartEndOppositeKey[rideStartEndKey];

  const newOppositeStartEndRideValue = addMinutes(
    rideDto[oppositeRideStartEndKey] || MIN_DATE_VALUE,
    timeOffsetMinutes,
  );

  // we assume that we have only two options for direction for ride
  const stationIdIndexResolved =
    rideDto?.direction === DirectionEnum.PickUp ? stations.length - 1 : 0;
  const metadataUpdated = updateMetadataStationsTime({
    rideDto,
    editStationId: stations[stationIdIndexResolved].id,
    timeShiftMinutes: timeOffsetMinutes,
  });

  return { newOppositeStartEndRideValue, metadataUpdated };
};
